import { Suspense } from 'react';
import clsx from 'clsx';
import { useBundleSelection } from '@/components/product/product-context';
import Image from 'next/image';
import Link from 'next/link';
import { SKELETON_PROPS } from '@/lib/constants/skeleton';
import { SvgPlus } from './SvgPlus';
import { resolveImageAltText } from '@/lib/utils';
import {
  getMatchingBundleOffers,
  getBundlePriceSummary
} from '@/lib/bundles/utils';

export function ToggleProductCard({ data }) {
  const { blockData, toggleIsUpsell } = data;

  return (
    <Suspense
      fallback={
        <ToggleProductCardView
          blockData={blockData}
          toggleIsUpsell={toggleIsUpsell}
          dataSkeleton={true}
        />
      }
    >
      <ResolvedToggleProductCard data={data} />
    </Suspense>
  );
}

/**
 * Reads the bundle selection, so it needs to be wrapped in a Suspense tag.
 */
function ResolvedToggleProductCard({ data }) {
  const { blockData, toggleIsUpsell, selectedCard, handleClick } = data;
  const { selectedOption, selectedProduct, priceSummary } =
    useBundleSelection();
  const selectedOptionValue = selectedOption?.value;
  const toggleHref = buildToggleHref(
    blockData.bundleData.url,
    selectedOption,
    selectedProduct
  );

  const priceDifference = getPriceDifference({
    currentPrice: priceSummary?.price,
    toggleBundleData: blockData.bundleData,
    selectedProduct,
    selectedOptionValue
  });
  const isSelected = selectedCard === 'toggle';
  const selectedOptionLabel = selectedOptionValue
    ? `(${selectedOptionValue})`
    : null;

  return (
    <ToggleProductCardView
      blockData={blockData}
      toggleIsUpsell={toggleIsUpsell}
      isSelected={isSelected}
      handleClick={handleClick}
      href={toggleHref}
      priceDifference={priceDifference}
      selectedOptionLabel={selectedOptionLabel}
    />
  );
}

function ToggleProductCardView({
  blockData,
  toggleIsUpsell,
  isSelected = false,
  handleClick,
  href,
  priceDifference,
  selectedOptionLabel,
  dataSkeleton = false
}) {
  const { details, image, bundleData, title } = blockData;
  const inputId = `select-${title}`;
  const cardMarkup = (
    <div
      className={clsx(
        'flex h-full flex-col justify-between border-2 px-3 py-2',
        href && 'cursor-pointer duration-300 ease-in-out',
        isSelected
          ? 'border-light-green'
          : dataSkeleton
            ? 'border-parlor-green-80'
            : 'border-parlor-green-80 hover:bg-parlor-green-115'
      )}
      onClick={href ? () => handleClick('toggle') : undefined}
      {...(dataSkeleton ? SKELETON_PROPS : {})}
    >
      <div>
        <div className="relative flex justify-between">
          <div className="sh12 text-white">{title}</div>
          <input
            type="radio"
            id={inputId}
            name="toggle-product"
            value={title}
            defaultChecked={isSelected}
            className={clsx(
              'absolute right-[4px] h-0 w-0',
              isSelected ? 'top-[5px]' : 'top-[6px]'
            )}
          />
          <label htmlFor={inputId}>
            <span className="sr-only">{title}</span>
          </label>
          <div
            className={clsx(
              'z-20 h-4 w-4 rounded-full',
              isSelected
                ? 'border-light-green bg-parlor-green-115 relative flex border'
                : 'bg-parlor-green-80 border border-white'
            )}
          >
            {isSelected && (
              <div className="bg-light-green z-30 mx-auto my-auto h-2 w-2 rounded-full"></div>
            )}
          </div>
        </div>
        <div className="flex min-h-6 items-center pb-[2px]">
          {priceDifference != null && selectedOptionLabel ? (
            <>
              <div
                className={clsx(
                  'price-s self-center pr-1',
                  toggleIsUpsell ? 'text-orange!' : 'text-dawn-grey-60!'
                )}
              >
                {toggleIsUpsell ? '+' : '-'}${priceDifference}
              </div>
              <div className="price-r-label text-dawn-grey-60">
                {selectedOptionLabel}
              </div>
            </>
          ) : (
            <div className="bg-parlor-green-80 h-4 w-24 rounded-sm"></div>
          )}
        </div>
      </div>
      <div>
        {image?.url && image?.dimension && (
          <Image
            src={`${image.url}?format=webp&disable=upscale`}
            width={image.dimension.width}
            height={image.dimension.height}
            alt={resolveImageAltText(image)}
            className="pb-[2px]"
          />
        )}
        {details.map((detail, index) => (
          <div key={index} className={clsx({ flex: toggleIsUpsell })}>
            {toggleIsUpsell && <SvgPlus />}
            <div className="body-s text-white">{detail.detail}</div>
          </div>
        ))}
      </div>
    </div>
  );

  if (!href) {
    return <div className="group">{cardMarkup}</div>;
  }

  return (
    <Link
      id="bundle-upsell-toggle-toggle-card"
      href={href}
      prefetch={false}
      title={bundleData.title}
      className="group"
      tabIndex={0}
      role="button"
      aria-pressed={isSelected}
      aria-label={title}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleClick('toggle');
        }
      }}
    >
      {cardMarkup}
    </Link>
  );
}

function buildToggleHref(url, selectedOption, selectedProduct) {
  const queryParams = new URLSearchParams();

  if (selectedOption?.name && selectedOption?.value) {
    queryParams.set(selectedOption.name, selectedOption.value);
  }

  if (selectedProduct?.shopifyProduct?.handle) {
    queryParams.set('selectedProduct', selectedProduct.shopifyProduct.handle);
  }

  queryParams.set('toggle', 'true');

  return `${url}?${queryParams.toString()}`;
}

/**
 * What switching to the toggle bundle would cost, as an absolute amount. The
 * sign is not encoded here — the card renders `+`/`-` from `toggleIsUpsell`.
 * Returns null until there is enough selection state to price either bundle.
 */
function getPriceDifference({
  currentPrice,
  toggleBundleData,
  selectedProduct,
  selectedOptionValue
}) {
  if (currentPrice == null || !selectedOptionValue) {
    return null;
  }

  const { bundlePricingMetafield } = toggleBundleData;
  const offers = getMatchingBundleOffers(
    bundlePricingMetafield,
    toggleBundleData.bundleProducts,
    selectedOptionValue,
    selectedProduct
  );
  const { price } = getBundlePriceSummary(
    bundlePricingMetafield,
    offers,
    selectedOptionValue
  );

  return Math.abs(currentPrice - price);
}
